import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { 
  Search, 
  ShoppingCart, 
  User, 
  CreditCard, 
  Printer, 
  Calculator, 
  Scan,
  DollarSign,
  Receipt,
  Calendar,
  Clock,
  Users,
  Package,
  Trash2,
  Plus,
  Minus,
  Star,
  Wifi,
  WifiOff,
  Database,
  BarChart3,
  Brain,
  Zap,
  Upload,
  Cloud,
  Shield,
  Globe
} from "lucide-react";
import { useProductStore } from "../stores/useProductStore";
import { getProductImages } from "../lib/imageUtils";

const POSSystem = () => {
  const { products, fetchAllProducts } = useProductStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [discount, setDiscount] = useState(0);
  // const [tax] = useState(18); // מע"מ 18% - לא בשימוש
  const [cashReceived, setCashReceived] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [connectionStatus] = useState("connected");
  const [activeSection, setActiveSection] = useState("products");
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showIntegrationPanel, setShowIntegrationPanel] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState({
    priority: "disconnected",
    sap: "disconnected",
    accounting: "connected"
  });
  const searchRef = useRef(null);

  // עדכון זמן כל שנייה
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // טעינת מוצרים
  useEffect(() => {
    fetchAllProducts();
  }, [fetchAllProducts]);

  // סינון מוצרים לפי חיפוש
  const filteredProducts = products && Array.isArray(products) ? products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  // חישוב סיכומים - המחירים כוללים מע"מ של 18%
  const totalWithVAT = selectedProducts.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const discountAmount = totalWithVAT * (discount / 100);
  const totalAfterDiscount = totalWithVAT - discountAmount;
  // חישוב מע"מ מתוך המחיר הכולל
  const subtotalBeforeVAT = totalAfterDiscount / 1.18;
  const taxAmount = totalAfterDiscount - subtotalBeforeVAT;
  const total = totalAfterDiscount;
  const change = cashReceived - total;

  // הוספת מוצר לעגלה
  const addToCart = (product) => {
    const existingItem = selectedProducts.find(item => item._id === product._id);
    if (existingItem) {
      setSelectedProducts(selectedProducts.map(item =>
        item._id === product._id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setSelectedProducts([...selectedProducts, { ...product, quantity: 1 }]);
    }
  };

  // עדכון כמות
  const updateQuantity = (id, quantity) => {
    if (quantity <= 0) {
      setSelectedProducts(selectedProducts.filter(item => item._id !== id));
    } else {
      setSelectedProducts(selectedProducts.map(item =>
        item._id === id ? { ...item, quantity } : item
      ));
    }
  };

  // מחיקת מוצר מהעגלה
  const removeFromCart = (id) => {
    setSelectedProducts(selectedProducts.filter(item => item._id !== id));
  };

  // עיבוד תשלום
  const processPayment = async () => {
    if (selectedProducts.length === 0) {
      alert("לא נבחרו מוצרים");
      return;
    }

    if (paymentMethod === "cash" && cashReceived < total) {
      alert("הסכום שהתקבל נמוך מהסכום הנדרש");
      return;
    }

    setIsProcessing(true);
    
    try {
      // בדיקת זמינות מלאי לפני עיבוד
      const stockValidation = await validateStockBeforePayment();
      if (!stockValidation.isValid) {
        alert(`מלאי לא מספיק:\n${stockValidation.errors.join('\n')}`);
        return;
      }

      // יצירת נתוני ההזמנה
      const orderData = {
        items: selectedProducts.map(item => ({
          _id: item._id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          sku: item.sku
        })),
        paymentMethod: paymentMethod,
        customerDetails: customer,
        amounts: {
          subtotal: subtotalBeforeVAT,
          discount: discountAmount,
          tax: taxAmount,
          total: total
        }
      };

      // יצירת סשן תשלום
      const sessionResponse = await fetch('/api/payments/create-israeli-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(orderData)
      });

      if (!sessionResponse.ok) {
        const errorData = await sessionResponse.json();
        throw new Error(errorData.message || 'שגיאה ביצירת סשן תשלום');
      }

      const { sessionId, transactionId } = await sessionResponse.json();

      // עיבוד התשלום בהתאם לסוג
      let paymentResult;
      
      if (paymentMethod === "cash") {
        // תשלום במזומן - עיבוד מיידי
        paymentResult = {
          success: true,
          transactionId: `CASH-${Date.now()}`,
          approvalNumber: `CASH-${Math.random().toString(36).substr(2, 6)}`,
          message: "תשלום במזומן התקבל"
        };
      } else if (paymentMethod === "card") {
        // תשלום בכרטיס - דמיון קורא כרטיסים
        paymentResult = await simulatePOSCardPayment({
          sessionId,
          transactionId,
          totalAmount: total
        });
      } else {
        // העברה בנקאית או אחר
        paymentResult = {
          success: true,
          transactionId: `TRANSFER-${Date.now()}`,
          approvalNumber: `TRF-${Math.random().toString(36).substr(2, 6)}`,
          message: "העברה בנקאית אושרה"
        };
      }

      if (paymentResult.success) {
        // ✅ עדכון מלאי ויצירת הזמנה בשרת
        const orderResponse = await fetch(`/payments/checkout-success/${paymentResult.transactionId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            items: selectedProducts,
            amounts: orderData.amounts,
            customer: customer
          })
        });

        if (!orderResponse.ok) {
          throw new Error("שגיאה ביצירת ההזמנה");
        }

        const orderResult = await orderResponse.json();
        
        // שליחה לכספית אם מחובר
        if (integrationStatus.priority === "connected" || integrationStatus.sap === "connected") {
          await sendToAccounting({
            ...orderData,
            transactionId: paymentResult.transactionId,
            receipt: orderResult.receipt
          });
        }
        
        // הצגת הודעת הצלחה עם פרטי הקבלה
        alert(`✅ התשלום עובד בהצלחה!
💳 מספר עסקה: ${paymentResult.transactionId}
🧾 מספר קבלה: ${orderResult.receipt?.receiptNumber || 'לא זמין'}
📦 המלאי עודכן במערכת
${paymentResult.approvalNumber ? `✔ מספר אישור: ${paymentResult.approvalNumber}` : ''}`);
        
        // רענון רשימת המוצרים כדי להציג מלאי מעודכן
        await fetchAllProducts();
        
        clearCart();
      } else {
        throw new Error(paymentResult.message || "התשלום נדחה");
      }
      
    } catch (error) {
      console.error("Payment error:", error);
      alert(`שגיאה בעיבוד התשלום: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // שליחה לכספית
  const sendToAccounting = async (data) => {
    const transactionData = {
      items: data.items,
      total: data.amounts.total,
      paymentMethod: data.paymentMethod,
      timestamp: new Date().toISOString(),
      customer: data.customerDetails,
      transactionId: data.transactionId,
      receipt: data.receipt
    };
    
    // כאן יהיה הקוד לשליחה לכספית
    console.log("שליחה לכספית:", transactionData);
  };

  // ניקוי עגלה
  const clearCart = () => {
    setSelectedProducts([]);
    setCustomer(null);
    setCashReceived(0);
    setDiscount(0);
    setSearchTerm("");
  };

  // אינטגרציה עם כספיות
  const connectToSystem = async (system) => {
    setIntegrationStatus(prev => ({
      ...prev,
      [system]: "connecting"
    }));
    
    // דמיון חיבור
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIntegrationStatus(prev => ({
      ...prev,
      [system]: "connected"
    }));
  };

  // בדיקת זמינות מלאי לפני תשלום
  const validateStockBeforePayment = async () => {
    const errors = [];
    
    for (const item of selectedProducts) {
      // מצא את המוצר ברשימה הנוכחית
      const currentProduct = products.find(p => p._id === item._id);
      if (!currentProduct) {
        errors.push(`מוצר ${item.name} לא נמצא במערכת`);
        continue;
      }
      
      if (!currentProduct.inStock || currentProduct.quantity < item.quantity) {
        errors.push(`${item.name}: מבוקש ${item.quantity}, זמין ${currentProduct.quantity}`);
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // דמיון תשלום בכרטיס במערכת POS
  const simulatePOSCardPayment = async ({ sessionId, transactionId, totalAmount }) => {
    return new Promise((resolve) => {
      // דמיון זמן עיבוד של קורא כרטיסים
      setTimeout(() => {
        const isApproved = Math.random() > 0.05; // 95% הצלחה במערכת POS
        
        if (isApproved) {
          resolve({
            success: true,
            transactionId,
            approvalNumber: Math.random().toString().substr(2, 6),
            cardCompany: "ישראכרט", // דמיון
            message: "תשלום בכרטיס אושר"
          });
        } else {
          resolve({
            success: false,
            message: "כרטיס נדחה - נסה שוב או השתמש בכרטיס אחר"
          });
        }
      }, 3000); // 3 שניות דמיון עיבוד
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-100 to-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gray-100 rounded-lg p-4 mb-6 shadow-2xl border border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="text-2xl font-bold text-brand-primary">
                SHA-SHA POS Pro
              </div>
              <div className="flex items-center space-x-2">
                {connectionStatus === "connected" ? (
                  <><Wifi className="w-5 h-5 text-green-400" /><span className="text-green-400">מחובר</span></>
                ) : (
                  <><WifiOff className="w-5 h-5 text-red-400" /><span className="text-red-400">מנותק</span></>
                )}
              </div>
              
              {/* כפתורי פעולות מתקדמות */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowIntegrationPanel(!showIntegrationPanel)}
                  className="p-2 ml-1 mr-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  title="ממשק כספית"
                >
                  <Database className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setActiveSection("reports")}
                  className="p-2 ml-1 mr-1 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  title="דוחות"
                >
                  <BarChart3 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setActiveSection("ai")}
                  className="p-2 ml-1 mr-1 bg-gradient-to-r from-pink-500 to-violet-600 text-white rounded-lg hover:from-pink-600 hover:to-violet-700 transition-colors"
                  title="בינה מלאכותית"
                >
                  <Brain className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowCustomerModal(true)}
                  className="p-2 ml-1 mr-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  title="ניהול לקוחות"
                >
                  <Users className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {customer && (
                <div className="text-green-400 text-sm flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  {customer.name}
                </div>
              )}
              <div className="text-gray-800 text-sm">
                <Clock className="w-5 h-5 inline mr-2 ml-2 text-gray-500" />
                {currentTime.toLocaleTimeString('he-IL')}
              </div>
              <div className="text-gray-800 text-sm">
                <Calendar className="w-5 h-5 inline mr-2 ml-2 text-gray-500" />
                {currentTime.toLocaleDateString('he-IL')}
              </div>
            </div>
          </div>
        </div>

        {/* Integration Panel */}
        {showIntegrationPanel && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800 rounded-lg p-6 mb-6 shadow-2xl border border-gray-700"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">אינטגרציה עם כספיות</h3>
              <button 
                onClick={() => setShowIntegrationPanel(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Priority */}
              <div className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <img src="/priority-logo.png" alt="Priority" className="w-8 h-8" onError={(e) => e.target.style.display = 'none'} />
                    <span className="text-white font-semibold">Priority</span>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${
                    integrationStatus.priority === "connected" ? "bg-green-400" :
                    integrationStatus.priority === "connecting" ? "bg-yellow-400" : "bg-red-400"
                  }`} />
                </div>
                <button
                  onClick={() => connectToSystem("priority")}
                  disabled={integrationStatus.priority === "connecting"}
                  className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {integrationStatus.priority === "connected" ? "מחובר" :
                   integrationStatus.priority === "connecting" ? "מתחבר..." : "התחבר"}
                </button>
              </div>

              {/* SAP */}
              <div className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Globe className="w-8 h-8 text-blue-500" />
                    <span className="text-white font-semibold">SAP ERP</span>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${
                    integrationStatus.sap === "connected" ? "bg-green-400" :
                    integrationStatus.sap === "connecting" ? "bg-yellow-400" : "bg-red-400"
                  }`} />
                </div>
                <button
                  onClick={() => connectToSystem("sap")}
                  disabled={integrationStatus.sap === "connecting"}
                  className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {integrationStatus.sap === "connected" ? "מחובר" :
                   integrationStatus.sap === "connecting" ? "מתחבר..." : "התחבר"}
                </button>
              </div>

              {/* חשבונאות כללית */}
              <div className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Calculator className="w-8 h-8 text-green-500" />
                    <span className="text-white font-semibold">חשבונאות</span>
                  </div>
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <button className="w-full bg-gray-600 text-white py-2 rounded cursor-not-allowed">
                  מחובר תמיד
                </button>
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Products */}
          <div className="lg:col-span-2">
            <div className="bg-gray-800 rounded-lg p-6 shadow-2xl border border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">רשימת מוצרים</h2>
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      ref={searchRef}
                      type="text"
                      placeholder="חפש מוצר או SKU..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-80 pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                  </div>
                  <button className="p-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/80 transition-colors">
                    <Scan className="w-5 h-5" />
                  </button>
                  
                  {/* AI כפתור */}
                  <button 
                    onClick={() => {
                      // AI המלצות על מוצרים
                      const aiRecommendation = products[Math.floor(Math.random() * products.length)];
                      if (aiRecommendation) {
                        addToCart(aiRecommendation);
                        alert(`AI המליץ על: ${aiRecommendation.name}`);
                      }
                    }}
                    className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors"
                    title="המלצת AI"
                  >
                    <Zap className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {filteredProducts.map((product) => {
                  const productImage = getProductImages(product)[0];
                  return (
                    <motion.div
                      key={product._id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-600 transition-colors border border-gray-600"
                      onClick={() => addToCart(product)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center">
                          {productImage ? (
                            <img
                              src={productImage}
                              alt={product.name}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <Package className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-white text-sm">{product.name}</h3>
                          <p className="text-gray-400 text-xs">{product.sku}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-brand-primary font-bold">₪{product.price}</span>
                            <span className="text-xs text-gray-400">{product.category}</span>
                          </div>
                          {product.isFeatured && (
                            <div className="flex items-center mt-1">
                              <Star className="w-3 h-3 text-yellow-400 fill-current" />
                              <span className="text-xs text-yellow-400 mr-1">מומלץ</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* AI Insights */}
              <div className="mt-6 p-4 bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-lg border border-purple-500/30">
                <div className="flex items-center space-x-2 mb-2">
                  <Brain className="w-5 h-5 text-purple-400" />
                  <span className="text-purple-400 font-semibold">AI תובנות</span>
                </div>
                <p className="text-gray-300 text-sm">
                  📈 המוצרים הנמכרים ביותר היום: שרשראות זהב
                  <br />
                  💎 המלצה: מוצרי כסף נמכרים טוב בשעות הערב
                  <br />
                  🎯 לקוח זה בדרך כלל קונה גם עגילים
                </p>
              </div>
            </div>
          </div>

          {/* Right Panel - Cart & Payment */}
          <div className="space-y-6">
            {/* Cart */}
            <div className="bg-gray-800 rounded-lg p-6 shadow-2xl border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">עגלת קניות</h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      // ייצוא לכספית
                      if (selectedProducts.length > 0) {
                        const exportData = {
                          items: selectedProducts,
                          total: total,
                          timestamp: new Date().toISOString()
                        };
                        console.log("ייצוא לכספית:", exportData);
                        alert("הנתונים יוצאו לכספית בהצלחה!");
                      }
                    }}
                    className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                    title="ייצוא לכספית"
                  >
                    <Upload className="w-5 h-5" />
                  </button>
                  <button
                    onClick={clearCart}
                    className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-3 max-h-60 overflow-y-auto">
                {selectedProducts.map((item) => (
                  <div key={item._id} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white text-sm">{item.name}</h4>
                      <p className="text-gray-400 text-xs">₪{item.price} x {item.quantity}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateQuantity(item._id, item.quantity - 1)}
                        className="p-1 text-gray-400 hover:text-white transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center text-white">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item._id, item.quantity + 1)}
                        className="p-1 text-gray-400 hover:text-white transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {selectedProducts.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>העגלה ריקה</p>
                </div>
              )}
            </div>

            {/* Payment Summary */}
            <div className="bg-gray-800 rounded-lg p-6 shadow-2xl border border-gray-700">
              <h3 className="text-lg font-bold text-white mb-4">סיכום תשלום</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between text-gray-300">
                  <span>סה&quot;כ מוצרים:</span>
                  <span>₪{totalWithVAT.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">הנחה:</span>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(Number(e.target.value))}
                      className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                      min="0"
                      max="100"
                    />
                    <span className="text-gray-300">%</span>
                  </div>
                </div>
                
                <div className="flex justify-between text-gray-300">
                  <span>מחיר לפני מע&quot;מ:</span>
                  <span>₪{subtotalBeforeVAT.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between text-gray-300">
                  <span>מע&quot;מ (18%):</span>
                  <span>₪{taxAmount.toFixed(2)}</span>
                </div>
                
                <div className="border-t border-gray-600 pt-3">
                  <div className="flex justify-between text-xl font-bold text-brand-primary">
                    <span>סה&quot;כ כולל מע&quot;מ:</span>
                    <span>₪{total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setPaymentMethod("cash")}
                    className={`p-3 rounded-lg border ${paymentMethod === "cash" 
                      ? "bg-brand-primary text-white border-brand-primary" 
                      : "bg-gray-700 text-gray-300 border-gray-600"
                    }`}
                  >
                    <DollarSign className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs">מזומן</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod("card")}
                    className={`p-3 rounded-lg border ${paymentMethod === "card" 
                      ? "bg-brand-primary text-white border-brand-primary" 
                      : "bg-gray-700 text-gray-300 border-gray-600"
                    }`}
                  >
                    <CreditCard className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs">אשראי</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod("transfer")}
                    className={`p-3 rounded-lg border ${paymentMethod === "transfer" 
                      ? "bg-brand-primary text-white border-brand-primary" 
                      : "bg-gray-700 text-gray-300 border-gray-600"
                    }`}
                  >
                    <Database className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs">העברה</span>
                  </button>
                </div>

                {paymentMethod === "cash" && (
                  <div className="space-y-2">
                    <label className="block text-sm text-gray-300">סכום שהתקבל:</label>
                    <input
                      type="number"
                      value={cashReceived}
                      onChange={(e) => setCashReceived(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      placeholder="₪0.00"
                    />
                    {cashReceived > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-300">עודף:</span>
                        <span className={change >= 0 ? "text-green-400" : "text-red-400"}>
                          ₪{Math.abs(change).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={processPayment}
                  disabled={selectedProducts.length === 0 || isProcessing || (paymentMethod === "cash" && cashReceived < total)}
                  className="w-full bg-brand-primary text-white py-3 rounded-lg font-bold hover:bg-brand-primary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>מעבד...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <Receipt className="w-5 h-5" />
                      <span>בצע תשלום</span>
                    </div>
                  )}
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {/* הדפסת קבלה */}}
                    className="bg-gray-700 text-white py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Printer className="w-4 h-4" />
                      <span>הדפס</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => {
                      // שליחה לענן
                      if (selectedProducts.length > 0) {
                        alert("הנתונים נשלחו לענן בהצלחה!");
                      }
                    }}
                    className="bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Cloud className="w-4 h-4" />
                      <span>ענן</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Status Indicators */}
            <div className="bg-gray-800 rounded-lg p-4 shadow-2xl border border-gray-700">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="flex flex-col items-center">
                  <Shield className="w-6 h-6 text-green-400 mb-1" />
                  <span className="text-xs text-green-400">מאובטח</span>
                </div>
                <div className="flex flex-col items-center">
                  <Zap className="w-6 h-6 text-yellow-400 mb-1" />
                  <span className="text-xs text-yellow-400">מהיר</span>
                </div>
                <div className="flex flex-col items-center">
                  <Brain className="w-6 h-6 text-purple-400 mb-1" />
                  <span className="text-xs text-purple-400">חכם</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Modal */}
        {showCustomerModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-gray-800 rounded-lg p-6 w-96 border border-gray-700"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">בחירת לקוח</h3>
                <button 
                  onClick={() => setShowCustomerModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-3">
                <button 
                  onClick={() => {
                    setCustomer({ name: "נטלי בדש", id: "053-333212789" });
                    setShowCustomerModal(false);
                  }}
                  className="w-full p-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 text-right"
                >
                  נטלי בדש - 053-333212789
                </button>
                <button 
                  onClick={() => {
                    setCustomer({ name: "לקוח אורח", id: "guest" });
                    setShowCustomerModal(false);
                  }}
                  className="w-full p-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 text-right"
                >
                  לקוח אורח
                </button>
                <button 
                  onClick={() => {
                    setCustomer(null);
                    setShowCustomerModal(false);
                  }}
                  className="w-full p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 text-right"
                >
                  ללא לקוח
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default POSSystem; 